// ANGEL FLIGHT — ObjectiveSystem: survive-timer win/lose.
//
// M1 (survive): hold out until the clock runs out with the gunner alive = WIN;
// hull down = LOSE. Drives the HUD countdown (throttled) and fires
// mission:cleared / mission:failed exactly once.

import type { EnemySystem } from './EnemySystem';
import type { GameContext, GameSystem } from './System';

export class ObjectiveSystem implements GameSystem {
  private ctx!: GameContext;
  private enemySystem: EnemySystem;
  private resolved = false;
  private emitAccum = 0;
  /** ENDLESS (Last Stand): no timer win — the run ends only when the bird goes
   *  down. The objective timer COUNTS UP (time survived) instead of down. */
  private endless = false;

  constructor(enemySystem: EnemySystem) {
    this.enemySystem = enemySystem;
  }

  private readonly onDown = (): void => this.fail('Angel-7 is down');

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.endless = ctx.mission.objective === 'endless';
    ctx.bus.on('hull:down', this.onDown);
    // Survive missions show a countdown from the limit; endless counts up from 0
    // (timeLimitMs 0 = "no limit / count up", read by the HUD).
    ctx.bus.emit('objective:timer', {
      timeLeftMs: this.endless ? 0 : ctx.mission.durationMs,
      timeLimitMs: this.endless ? 0 : ctx.mission.durationMs,
    });
  }

  update(dt: number): void {
    if (this.resolved) return;
    const ctx = this.ctx;
    this.emitAccum += dt;
    if (this.endless) {
      // Count UP: report time survived. Never timer-clears — hull:down → fail.
      if (this.emitAccum >= 200) {
        this.emitAccum = 0;
        ctx.bus.emit('objective:timer', { timeLeftMs: ctx.run.elapsedMs, timeLimitMs: 0 });
      }
      return;
    }
    const left = Math.max(0, ctx.mission.durationMs - ctx.run.elapsedMs);
    if (this.emitAccum >= 200) {
      this.emitAccum = 0;
      ctx.bus.emit('objective:timer', { timeLeftMs: left, timeLimitMs: ctx.mission.durationMs });
    }
    if (left <= 0 && (ctx.gunner?.hull ?? 0) > 0) this.clear();
  }

  private clear(): void {
    if (this.resolved) return;
    this.resolved = true;
    void this.enemySystem.allWavesCleared();
    this.ctx.bus.emit('mission:cleared', { missionId: this.ctx.mission.id });
  }

  private fail(reason: string): void {
    if (this.resolved) return;
    this.resolved = true;
    this.ctx.bus.emit('mission:failed', { missionId: this.ctx.mission.id, reason });
  }

  destroy(): void {
    this.ctx?.bus.off('hull:down', this.onDown);
  }
}
